import { useEffect } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { formatCurrency } from '../../utils/amountUtils';

function PortalPaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const { publicId, token } = useOutletContext();
  const paymentResult = location.state?.paymentResult;
  const invoices = location.state?.invoices || [];

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  console.log('🎯 PortalPaymentResult - Received data:', {
    hasPaymentResult: !!paymentResult,
    paymentResult: paymentResult,
    invoiceCount: invoices.length,
    publicId,
    token,
    locationState: location.state
  });

  useEffect(() => {
    // If no payment result, redirect to invoices after a small delay to avoid race conditions
    if (!paymentResult) {
      console.log('No payment result found, redirecting to invoices');
      const timer = setTimeout(() => {
        navigate('../invoices');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [paymentResult, navigate, publicId, token]);


  // Intentionally NOT using utils/dateUtils.formatDate: paymentDate is a
  // TIMESTAMPTZ (full ISO timestamp), not a DATE. We want to display the
  // exact time of payment ("May 6, 2026, 06:00 AM") — routing through
  // parseDateSafely would truncate to date-only and lose the time component.
  // Full ISO timestamps don't have the YYYY-MM-DD UTC parse trap because
  // they encode a precise moment.
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownloadReceipt = () => {
    // This will be implemented when receipt generation is ready
    console.log('Download receipt for transaction:', paymentResult.transactionId);
  };


  // Show loading state while checking for payment result
  if (!paymentResult) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Loading Payment Result...</h1>
          <p className="text-gray-600">
            Please wait while we verify your payment.
          </p>
        </div>
      </div>
    );
  }

  const isSuccess = paymentResult.success;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Result Icon and Message */}
      <div className="text-center">
        {isSuccess ? (
          <>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
            <p className="text-gray-600">
              Your payment has been processed successfully.
            </p>
          </>
        ) : (
          <>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Failed</h1>
            <p className="text-gray-600">
              {paymentResult.error || 'There was an issue processing your payment.'}
            </p>
          </>
        )}
      </div>

      {/* Payment Details */}
      {isSuccess && (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Payment Details</h2>
          </div>
          
          <div className="p-6 space-y-4">
            {/* Transaction Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Transaction ID</p>
                <p className="font-medium text-gray-900">{paymentResult.transactionId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Date</p>
                <p className="font-medium text-gray-900">{formatDate(paymentResult.paymentDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Method</p>
                <p className="font-medium text-gray-900">Credit Card</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  {paymentResult.surchargeAmount > 0 ? 'Invoice Amount' : 'Total Amount'}
                </p>
                <p className="font-medium text-gray-900">{formatCurrency(paymentResult.totalAmount)}</p>
              </div>
              {paymentResult.surchargeAmount > 0 && (
                <>
                  <div>
                    <p className="text-sm text-gray-600">Surcharge</p>
                    <p className="font-medium text-amber-600">+{formatCurrency(paymentResult.surchargeAmount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Charged</p>
                    <p className="font-medium text-gray-900">
                      {formatCurrency(
                        parseFloat(paymentResult.totalAmount || 0) + parseFloat(paymentResult.surchargeAmount || 0)
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Paid Invoices */}
            {paymentResult.payments && paymentResult.payments.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Paid Invoices</h3>
                <div className="space-y-2">
                  {paymentResult.payments.map((payment, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="font-medium text-gray-900">{payment.invoiceNumber}</p>
                        <p className="text-sm text-gray-600">Amount Paid: {formatCurrency(payment.amountPaid)}</p>
                      </div>
                      <span className="text-sm font-medium text-green-600">Paid</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleDownloadReceipt}
                className="flex-1 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Receipt
              </button>
              <button
                onClick={() => navigate('../payment-history')}
                className="flex-1 px-4 py-2 bg-brand text-white font-medium rounded-lg hover:bg-brand-hover transition-colors"
              >
                View Invoice History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Failure Actions */}
      {!isSuccess && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">What would you like to do?</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate(-1)}
              className="w-full px-4 py-2 bg-brand text-white font-medium rounded-lg hover:bg-brand-hover transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('../invoices')}
              className="w-full px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back to Invoices
            </button>
          </div>
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Need help?</strong> Please contact our support team if you continue to experience issues with your payment.
            </p>
          </div>
        </div>
      )}

      {/* Additional Information */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-sm font-medium text-gray-900 mb-2">What's Next?</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          {isSuccess ? (
            <>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                A confirmation email has been sent to your registered email address
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Your account balance has been updated
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                You can download your receipt and updated invoices anytime
              </li>
            </>
          ) : (
            <>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Please verify your payment information and try again
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Contact your bank if the problem persists
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Your invoices remain unpaid and accessible in your portal
              </li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
}

export default PortalPaymentResult;